import { Drag } from '@e3dc-react/icons'
import { Box, IconButton, Menu, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { AreaProperties } from '../types'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { makeStyles } from 'tss-react/mui'
import { DASHBOARD_EDITOR_HIDDEN_BUTTON_ID, DASHBOARD_EDITOR_VISIBLE_BUTTON_ID } from 'src/Libs/qa/ids'
import VisibilityIcon from 'src/Elements/VisibilityIcon'

enum DragState {
  idle = 'idle',
  dragging = 'dragging',
  over = 'over',
}

interface StyleProps {
  dragState: DragState
  disabled: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { dragState, disabled }) => ({
  editorItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: (() => {
      if (disabled) return theme.palette.grey.grey09
      if (dragState === DragState.dragging) return theme.palette.shades.shade03
      return theme.palette.shades.shade04
    })(),
    border: `1px solid ${theme.palette.shades.shade03}`,
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  drag: {
    '&:hover': {
      cursor: 'grab',
    },
  },
}))

/**
 * DashboardArea component for the dashboard editor
 */
const DashboardArea: React.FC<AreaProperties> = ({ name, areaKey, position, hidden, setHidden, children, getMenuItems, disabled, dataQa }) => {
  const ref = useRef(null)
  const [dragState, setDragState] = useState<DragState>(DragState.idle)
  const { classes } = useStyles({ dragState, disabled })
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const visibilityIconDataQaId = hidden ? DASHBOARD_EDITOR_HIDDEN_BUTTON_ID : DASHBOARD_EDITOR_VISIBLE_BUTTON_ID

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  useEffect(() => {
    const element = ref.current

    if (!element) return

    return draggable({
      element,
      getInitialData: () => ({ areaKey, position }),
      onDragStart: () => setDragState(DragState.dragging),
      onDrop: () => setDragState(DragState.idle),
    })
  }, [areaKey, position, disabled])

  return (
    <Box className={classes.editorItem} ref={ref} data-qa={dataQa}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <IconButton
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          color="inherit"
        >
          <Drag className={classes.drag} />
        </IconButton>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {getMenuItems(handleClose)}
        </Menu>
        <Typography sx={disabled ? { color: 'grey.grey03' } : {}}>{name}</Typography>
        <VisibilityIcon isHidden={hidden} isDisabled={disabled} onClick={() => setHidden(!hidden)} data-qa={visibilityIconDataQaId} />
      </Box>
      {children}
    </Box>
  )
}

export default DashboardArea
